/* eslint-disable class-methods-use-this */

'use strict';

import Modal from './Modal';

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
const AUTH_LINKS = '.b-wishlist_gate-register, .b-wishlist_gate-login';

/**
 * Login and create-account prompt for a gated guest save.
 *
 * `Modal` is trigger-bound: it binds a click handler on its own element and
 * reads the content URL from that element's attributes. This gate has no
 * trigger, because it opens from a save handler, so this subclass supplies a
 * detached element to carry the URL and drives the inherited AJAX and show
 * sequence directly.
 *
 * It also adds focus management, which `Modal` does not provide. Content is
 * appended after `modal('show')` has already been called, so initial focus can
 * otherwise land before the content exists.
 */
export default class WishlistGateModal extends Modal {
    constructor(config = {}) {
        const $carrier = $('<a>')
            .attr('data-url', config.contentUrl)
            .attr('aria-hidden', 'true')
            .css('display', 'none');

        super($carrier, config);

        // `Modal.init` would re-read config from the (empty) element and
        // discard what was passed in, so it is deliberately not called.
        this.config = $.extend(true, {}, this.configDefault, config);
        this.targetID = 'wishlistGateModal-' + new Date().getTime();
        this.onShown = this.onShown.bind(this);
        this.onHidden = this.onHidden.bind(this);
        this.onKeydown = this.onKeydown.bind(this);
        this.onAuthLinkClick = this.onAuthLinkClick.bind(this);
        // Set when the customer leaves through the prompt's own login or
        // register link, which is the one way of closing this modal that must
        // not cancel the pending action.
        this.authenticating = false;
    }

    /**
     * Opens the prompt.
     *
     * @returns {void}
     */
    open() {
        this.previousActiveElement = document.activeElement;

        this.createTarget();
        this.$target
            .addClass('g-modal-wishlist-gate')
            .attr('aria-modal', 'true')
            .attr('aria-labelledby', 'wishlistGateTitle');

        this.$target.on('shown.bs.modal', this.onShown);
        this.$target.on('hidden.bs.modal', this.onHidden);
        this.$target.on('keydown', this.onKeydown);
        // Delegated, because the prompt's content arrives after this runs.
        // Covers both authentication paths: the hosted UI navigates away, and
        // the in-page modal hides this one, and neither is a dismissal.
        this.$target.on('click', AUTH_LINKS, this.onAuthLinkClick);

        this.fillModalElement();
    }

    onAuthLinkClick() {
        this.authenticating = true;
    }

    onShown() {
        // Focus the first interactive element in the loaded content. Falls back
        // to the dialog itself when the content has not arrived, so focus is
        // never left behind on the page underneath.
        const $focusable = this.$target.find(FOCUSABLE).filter(':visible');
        if ($focusable.length) {
            $focusable.first().trigger('focus');
        } else {
            this.$target.attr('tabindex', '-1').trigger('focus');
        }
    }

    onKeydown(event) {
        if (event.key !== 'Tab') {
            return;
        }

        const $focusable = this.$target.find(FOCUSABLE).filter(':visible');
        if (!$focusable.length) {
            return;
        }

        const first = $focusable.first()[0];
        const last = $focusable.last()[0];

        if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
        }
    }

    /**
     * WCAG 2.4.3. `afterClose` runs first because it dismisses the Search
     * suggestions and the Shop This Outfit overlay, and the element that had
     * focus when the prompt opened is usually inside one of them. Restoring
     * focus before that would put it on a node about to be hidden or removed,
     * and the browser would silently drop it to `body`. Restoring after lets
     * the visibility check below catch that case and fall back to something
     * the customer can still see.
     *
     * @returns {void}
     */
    onHidden() {
        this.afterClose();
        this.cancelPendingAction();
        this.restoreFocus();
        this.destroyTarget();
    }

    /**
     * Discards the pending save, bulk save or list return when the prompt is
     * dismissed rather than acted on.
     *
     * The record outlives the modal otherwise. A guest who closes the prompt
     * and then signs in through the header inside the intent's fifteen minutes
     * has that unrelated sign-in complete the save they backed out of, or be
     * redirected to the saved-items list. Closing the prompt is the customer
     * saying no, so the record goes with it.
     *
     * Required lazily to keep the module graph acyclic: `wishlistGate` loads
     * this class at module scope, so importing it at the top here would close
     * the loop.
     *
     * @returns {void}
     */
    cancelPendingAction() {
        if (this.authenticating) {
            return;
        }

        require('../../wishlist/wishlistGate').cancelPendingAction();
    }

    /**
     * @returns {void}
     */
    restoreFocus() {
        const previous = this.previousActiveElement;
        const $previous = previous ? $(previous) : $();
        // A modal that `afterClose` has just dismissed still passes `:visible`
        // for the length of its fade, but Bootstrap drops `show` synchronously
        // in `hide()`, so that class is the reliable signal. Our own target is
        // never a match: `previousActiveElement` is captured before it exists.
        const inClosingModal = $previous.closest('.modal').not('.show').length > 0;
        const usable = previous
            && typeof previous.focus === 'function'
            && document.body.contains(previous)
            && !inClosingModal
            && $previous.is(':visible');

        if (usable) {
            previous.focus();
            return;
        }

        // Nothing to go back to. Park focus at the top of the main content
        // rather than leaving it on `body`, where the next Tab would restart
        // from the browser chrome.
        const $fallback = $('main, [role="main"], #maincontent').first();
        if ($fallback.length) {
            $fallback.attr('tabindex', '-1').trigger('focus');
        }
    }

    destroyTarget() {
        if (this.$target) {
            this.$target.off('shown.bs.modal', this.onShown);
            this.$target.off('hidden.bs.modal', this.onHidden);
            this.$target.off('keydown', this.onKeydown);
            this.$target.off('click', AUTH_LINKS, this.onAuthLinkClick);
            this.$target.remove();
            this.$target = null;
        }
        $('body > .modal-backdrop').remove();
    }

    /**
     * FR-05 and FR-15. Dismissing the prompt from the Search Bar or Shop This
     * Outfit overlay also dismisses that overlay, so the customer is returned
     * to the underlying page with no message and no save. Keyed on the
     * originating surface rather than on inspecting the current URL.
     *
     * @returns {void}
     */
    afterClose() {
        if (this.config.surface === 'search' || this.config.surface === 'sto') {
            $('body').trigger('wishlistGate:dismissOverlay', { surface: this.config.surface });
        }
    }
}
